import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
} from '@patternfly/react-core';
import type { ResourceStepProps } from './resourceStep';

/**
 * DNS-zone step: review only.
 *
 * The zone is one thing — its records are never listed here (a zone can hold tens of thousands, and
 * the aggregate read deliberately returns counts only), so selecting the type in step 1 selects the
 * whole zone. What this step owes the user is what deleting it actually does, which depends on the
 * backend's `action`: `delete` removes the zone from the nameservers, `unbind` only drops the binding
 * because no zone is there to remove. Plan §5.3 and `docs/api-types/domain-names.md` → Deactivate DNS
 * zone.
 */
const DnsZoneStep: React.FunctionComponent<ResourceStepProps> = ({ domain, resources, elements }) => {
  const zone = resources.dns_zone;
  const element = elements[0] ?? null;

  if (zone === null || element === null) {
    return (
      <Content component="p">Ancalagon does not host a DNS zone for {domain.name}, so nothing is removed here.</Content>
    );
  }

  const recordsByType =
    zone.record_count_by_type === null
      ? null
      : Object.entries(zone.record_count_by_type)
          .map(([type, count]) => `${type} ${count}`)
          .join(' · ');

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        This is the DNS zone Ancalagon hosts for <strong>{domain.name}</strong>. It is removed as a whole — individual
        records cannot be kept.
      </Content>

      <DescriptionList isHorizontal isCompact aria-label={`DNS zone of ${domain.name}`}>
        <DescriptionListGroup>
          <DescriptionListTerm>Nameservers</DescriptionListTerm>
          <DescriptionListDescription>{zone.dns_endpoint_name ?? 'Unknown endpoint'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Zone</DescriptionListTerm>
          <DescriptionListDescription>
            {zone.zone_state === 'configured'
              ? 'Live on its nameservers'
              : zone.zone_state === 'not_configured'
                ? 'No zone on the nameservers — only the binding is left'
                : 'Could not be read from its nameservers'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {zone.record_count !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>Records</DescriptionListTerm>
            <DescriptionListDescription>
              {zone.record_count}
              {recordsByType !== null && recordsByType !== '' ? ` (${recordsByType})` : ''}
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
        {zone.serial !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>Serial</DescriptionListTerm>
            <DescriptionListDescription>{zone.serial}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
        {zone.zone_error !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>Problem</DescriptionListTerm>
            <DescriptionListDescription>{zone.zone_error}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
      </DescriptionList>

      {element.deletable && element.action === 'delete' && (
        <Alert
          variant="warning"
          isInline
          component="h2"
          title="The zone and all its records are deleted"
          className="pf-v6-u-mt-md"
        >
          <p>
            The zone is removed from {zone.dns_endpoint_name ?? 'its nameservers'} and cannot be restored. Make sure the
            domain&apos;s NS records already point elsewhere, or the domain stops resolving.
          </p>
        </Alert>
      )}

      {element.deletable && element.action === 'unbind' && (
        <Alert variant="info" isInline component="h2" title="Only the binding is removed" className="pf-v6-u-mt-md">
          <p>
            No zone is present on {zone.dns_endpoint_name ?? 'the nameservers'}, so nothing is deleted there. The domain
            simply stops being managed by Ancalagon&apos;s DNS.
          </p>
        </Alert>
      )}

      {!element.deletable && (
        <Alert variant="warning" isInline component="h2" title="This zone is kept" className="pf-v6-u-mt-md">
          <p>{element.blockedReason ?? 'This zone cannot be deleted right now.'}</p>
        </Alert>
      )}

      <Content component="p" className="pf-v6-u-mt-md">
        <Button
          variant="link"
          isInline
          component={(props) => <Link {...props} to={`/datacenter/domains/${zone.domain_name_id}`} />}
        >
          Review the records on the domain&apos;s DNS Zone tab
        </Button>{' '}
        before continuing.
      </Content>
    </div>
  );
};

export default DnsZoneStep;
